import { useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { MapPin, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STR = {
  no: {
    title: "Del posisjon med oss",
    body: "Hvis du trenger hjelp eller bare vil at vi vet hvor du er, kan du sende oss posisjonen din med ett trykk. Vi ser den når vi logger inn – trenger du rask kontakt, send en WhatsApp til manager Joakim Maylove på +47 900 21 105.",
    button: "Del min posisjon",
    sharing: "Henter posisjon…",
    shared: "Takk! Vi har mottatt posisjonen din.",
    error: "Kunne ikke hente posisjon. Sjekk at du har gitt nettleseren tilgang.",
    optional: "Valgfri melding (f.eks. «Trenger hjelp», «Sitter fast i sluse»)",
    again: "Del en gang til",
  },
  en: {
    title: "Share your location with us",
    body: "If you need help or just want us to know where you are, you can send us your location with one tap. We see it when we log in – for a fast response, send a WhatsApp to manager Joakim Maylove on +47 900 21 105.",
    button: "Share my location",
    sharing: "Fetching location…",
    shared: "Thank you! We've received your location.",
    error: "Could not fetch location. Make sure you've allowed the browser to use it.",
    optional: "Optional message (e.g. \"need assistance\", \"stuck at lock\")",
    again: "Share again",
  },
};

export default function ShareLocationCard({ lang = "no" }) {
  const t = STR[lang] || STR.no;
  const [busy, setBusy] = useState(false);
  const [shared, setShared] = useState(false);
  const [note, setNote] = useState("");

  const share = () => {
    if (!navigator.geolocation) {
      toast.error(t.error);
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/portal/share-location", {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            note: note.trim(),
          });
          setShared(true);
          setNote("");
          toast.success(t.shared);
        } catch (e) {
          toast.error(e?.response?.data?.detail || t.error);
        } finally {
          setBusy(false);
        }
      },
      () => {
        setBusy(false);
        toast.error(t.error);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );
  };

  return (
    <Card className="p-6 sm:p-8" data-testid="share-location-card">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-[#1DA3B3]/10 rounded-xl text-[#1DA3B3]">
          <MapPin className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold text-[#0C1824]">{t.title}</h2>
          <p className="text-sm text-[#2B3F4D] mt-1.5 max-w-xl leading-relaxed">{t.body}</p>

          {!shared && (
            <div className="mt-4">
              <Label className="text-xs text-[#A57F39]">{t.optional}</Label>
              <Textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder=""
                data-testid="share-location-note"
                className="mt-1"
              />
            </div>
          )}

          <div className="mt-4">
            {!shared ? (
              <Button
                onClick={share}
                disabled={busy}
                data-testid="share-location-button"
                className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white h-11 px-5"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                {busy ? t.sharing : t.button}
              </Button>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2 text-emerald-600 text-sm font-semibold">
                  <Check className="w-4 h-4" /> {t.shared}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShared(false)}
                  data-testid="share-location-again"
                >
                  {t.again}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
